import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Plus } from 'lucide-react';
import { GroupList } from './GroupList';
import { GroupChat } from './GroupChat';
import { GroupPosts } from './GroupPosts';
import { CreateGroupDialog } from './CreateGroupDialog';
import { EditGroupDialog } from './EditGroupDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { subscribeToGroupUpdates } from '@/lib/firebase/queries/groups';
import { getUserPersonalPermissions } from '@/lib/firebase/queries/permissions';
import { useAuth } from '@/contexts/AuthContext';
import type { Group } from '@/types/support';

interface SupportGroupsProps {
  organizationId?: string;
}

export function SupportGroups({ organizationId }: SupportGroupsProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [showPosts, setShowPosts] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  // Groups created here are personal (no organization), so only the super
  // admin's setting applies — an organization's override cannot block them.
  const [canCreatePersonal, setCanCreatePersonal] = useState(false);
  useEffect(() => {
    if (!currentUser?.uid) return;
    let alive = true;
    getUserPersonalPermissions(currentUser.uid).then((p) => {
      if (alive) setCanCreatePersonal(p.actions.createSupportGroup !== false);
    });
    return () => { alive = false; };
  }, [currentUser?.uid]);

  useEffect(() => {
    const unsubscribe = subscribeToGroupUpdates(
      (updatedGroups) => {
        setGroups(updatedGroups);
      },
      (error) => {
        console.error('Error fetching groups:', error);
      },
      organizationId,
      // Mobile parity: the active organization's groups + personal groups.
      { includePersonal: true }
    );

    return () => unsubscribe();
  }, [organizationId]);

  const handleCreateGroupSuccess = (createdGroup?: Group) => {
    // Optimistic update: Add the group to the list immediately
    if (createdGroup) {
      setGroups(prev => {
        // Check if group already exists (shouldn't, but safety check)
        const exists = prev.some(g => g.id === createdGroup.id);
        if (exists) return prev;
        // Add to the beginning of the list
        return [createdGroup, ...prev];
      });
    }
    setShowCreateGroup(false);
  };

  const handleEditGroupSuccess = () => {
    setShowEditGroup(false);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {t('support.supportGroups')}
          </CardTitle>
          {canCreatePersonal && (
            <Button 
              variant="outline"
              onClick={() => setShowCreateGroup(true)}
              className="bg-white hover:bg-gray-100"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('support.createGroup')}</span>
              <span className="sm:hidden">Create</span>
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        <GroupList 
          groups={groups}
          onOpenChat={(group) => {
            setSelectedGroup(group);
            setShowChat(true);
          }}
          onOpenPosts={(group) => {
            setSelectedGroup(group);
            setShowPosts(true);
          }}
          onEditGroup={(group) => {
            setSelectedGroup(group);
            setShowEditGroup(true);
          }}
        />
      </CardContent>

      {showChat && selectedGroup && (
        <GroupChat
          group={selectedGroup}
          open={showChat}
          onOpenChange={setShowChat}
        />
      )}

      {showPosts && selectedGroup && (
        <GroupPosts
          group={selectedGroup}
          open={showPosts}
          onOpenChange={setShowPosts}
        />
      )}

      <CreateGroupDialog
        open={showCreateGroup}
        onOpenChange={setShowCreateGroup}
        onSuccess={handleCreateGroupSuccess}
      />

      {selectedGroup && (
        <EditGroupDialog
          group={selectedGroup}
          open={showEditGroup}
          onOpenChange={setShowEditGroup}
          onSuccess={handleEditGroupSuccess}
        />
      )}
    </Card>
  );
}